import Choices, {Choices as ChoicesLib} from 'choices.js';

import Component from '../../component/component';
import Icon from '../../icon/icon';

import '../textfield/textfield';

import './select.scss';

export interface ISelectSettings {
    multiselect: false,
    noResultsText: string;
    search: boolean;
    removeItemText: string;
}

export default class Select extends Component {
    static initSelector: string = '.select';

    public settings: ISelectSettings;
    public input: HTMLInputElement;
    public choices: Choices;
    public multiple: boolean;
    public isOpen: boolean;
    public isDisabled: boolean;
    public descriptions: {[key: string]: string};

    constructor(target: HTMLElement) {
        super(target);

        this.input = this.element.querySelector('.select__input');
        this.descriptions = {};

        this.settings = {
            multiselect: false,
            noResultsText: (this.input.getAttribute('data-no-results-text') || 'No results found'),
            removeItemText: (this.input.getAttribute('data-remove-item-text') || 'Remove item'),
            search: !!this.input.getAttribute('data-search'),
        };

        this.element.querySelectorAll('option').forEach((element: HTMLOptionElement) => {
            if (typeof element.getAttribute('data-description') !== undefined) {
                this.descriptions[element.value] = element.getAttribute('data-description');
            }
        });

        this.isOpen = false;
        this.isDisabled = false;
        this.multiple = !!this.input.getAttribute('multiple');

        if (!this.element.classList.contains('select--simple')) {
            this.init();
        }
    }

    init(): void {
        if (this.input.hasAttribute('disabled')) {
            this.input.removeAttribute('disabled');
            this.isDisabled = true;
        }

        this.choices = new Choices(this.input, {
            callbackOnCreateTemplates: (template: ChoicesLib.Types.strToEl): Partial<ChoicesLib.Templates> => ({
                choice: (classNames: ChoicesLib.ClassNames, data: ChoicesLib.Choice): HTMLElement => template(`
                    <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable} ${data.selected ? classNames.selectedState : ''}"
                        data-choice
                        ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
                        data-id="${data.id}"
                        data-value="${data.value}"
                        ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}
                    >
                        <span class="select__text">${data.label}${this.descriptions[data.value] ? '<span class="select__description"> ' + this.descriptions[data.value] + '</span>' : ''}${Icon.render('check', '', 'select__check-icon')}
                    </div>`) as HTMLElement,
                dropdown: (classNames: ChoicesLib.ClassNames): HTMLElement => template(`
                        <div class="${classNames.list} ${classNames.listDropdown}" aria-expanded="false">
                            ${Icon.render('search', '', 'select__search-icon')}
                        </div>
                    `) as HTMLElement,
                item: (classNames: ChoicesLib.ClassNames, data: ChoicesLib.Choice): HTMLElement => template(`
                        <div class="${classNames.item}" data-item data-id="${data.id}" data-value="${data.value}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''} data-deletable>
                            ${data.label}
                            ${this.descriptions[data.value] ? '<span class="select__description"> ' + this.descriptions[data.value] + '</span>' : ''}
                            <button type="button" class="${classNames.button}" aria-label="${this.settings.removeItemText}" data-button>${Icon.render('close', '', 'select__close-icon')}</button>
                        </div>
                    `) as HTMLElement,
            }),
            classNames: {
                activeState: 'is-active',
                button: 'select__remove',
                containerInner: 'textfield__input select__inner',
                containerOuter: 'select__container',
                disabledState: 'is-disabled',
                flippedState: 'is-flipped',
                focusState: 'is-focused',
                group: 'select__group',
                groupHeading: 'select__group-heading',
                highlightedState: 'select__item--highlighted',
                input: 'select__choices-input',
                inputCloned: 'select__choices-input--cloned',
                item: 'select__item',
                itemChoice: 'select__item--choice',
                itemDisabled: 'select__item--disabled',
                itemSelectable: 'select__item--selectable',
                list: 'select__list',
                listDropdown: 'select__dropdown',
                listItems: 'select__list--multiple',
                listSingle: 'select__list--single',
                loadingState: 'is-loading',
                noChoices: 'has-no-choices',
                noResults: 'has-no-results',
                openState: 'is-open',
                placeholder: 'select__placeholder',
                selectedState: 'is-selected',
            },
            editItems: false,
            itemSelectText: '',
            noResultsText: this.settings.noResultsText,
            removeItemButton: true,
            removeItems: true,
            renderSelectedChoices: 'always',
            searchChoices: this.settings.search,
            searchEnabled: this.settings.search,
            shouldSort: false,
        });

        if (this.isDisabled) {
            this.choices.disable();
            this.input.disabled = true;
        }

        this.addEventListeners();
    }

    addEventListeners(): void {
        // Multiselect context
        if (this.multiple) {
            // Deselect a selected item when clicking on it.
            this.input.addEventListener('choice', (event: CustomEvent) => {
                const values: string[] | string = this.choices.getValue(true);
                const value: string = event.detail.choice.value;

                if (values?.includes(value)) {
                    setTimeout((): Choices => this.choices.removeActiveItemsByValue(value));
                }
            });

            // Close dropdown when clicking on empty space between currently selected items in select list.
            this.element.querySelector('.select__inner > .select__list').addEventListener('click', (): void => {
                if (this.isOpen) {
                    this.choices.hideDropdown();
                }
            });
        }

        this.input.addEventListener('hideDropdown', (): void => {
            this.isOpen = false;

            // Clear search input when closing dropdown
            if (this.settings.search) {
                this.choices.clearInput();
            }
        });

        this.input.addEventListener('showDropdown', (): void => {
            // Focus search input when opening dropdown if search enabled.
            if (this.settings.search) {
                const dropdown: HTMLElement = this.element.querySelector('.select__dropdown .select__choices-input--cloned');

                if (dropdown) {
                    dropdown.focus();
                }
            }

            this.isOpen = true;
        });

        this.element.addEventListener('enableSelect', (): void => {
            this.enableSelect();
        });
    }

    enableSelect(): void {
        this.choices.enable();
        this.element.classList.remove('is-disabled');
        this.input.removeAttribute('disabled');
    }
}
